import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { InvoiceLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    invoiceLine: InvoiceLineModel;
    extendedStyles?: InvoiceDetailsLineCardStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing:
        state.context.showPricing && state.context?.session?.properties?.canShowPrice?.toLowerCase() === "true",
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface InvoiceDetailsLineCardStyles {
    container?: GridContainerProps;
    imageItem?: GridItemProps;
    imageLink?: LinkPresentationProps;
    image?: LazyImageProps;
    infoItem?: GridItemProps;
    infoInnerContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    brandItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    productNameItem?: GridItemProps;
    productNameLink?: LinkPresentationProps;
    numbersItem?: GridItemProps;
    numbersInnerContainer?: GridContainerProps;
    partItem?: GridItemProps;
    partStyles?: SmallHeadingAndTextStyles;
    myPartItem?: GridItemProps;
    myPartStyles?: SmallHeadingAndTextStyles;
    mgfItem?: GridItemProps;
    mgfGridContainer?: GridContainerProps;
    mgfStyles?: SmallHeadingAndTextStyles;
    notesItem?: GridItemProps;
    notesStyles?: SmallHeadingAndTextStyles;
    infoRightColumn?: GridItemProps;
    infoRightColumnContainer?: GridContainerProps;
    priceItem?: GridItemProps;
    priceStyles?: SmallHeadingAndTextStyles;
    qtyItem?: GridItemProps;
    qtyStyles?: SmallHeadingAndTextStyles;
    totalItem?: GridItemProps;
    totalStyles?: SmallHeadingAndTextStyles;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productDescriptionText?: TypographyPresentationProps;
}

export const cardStyles: InvoiceDetailsLineCardStyles = {
    imageItem: {
        width: 2,
        css: css`
            @media print {
                padding: 10px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 15px 0 15px 10px;
                        `,
                        css`
                            padding: 15px 0 15px 15px;
                        `,
                        css`
                            padding: 15px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    image: {
        height: "auto",
    },
    infoItem: {
        width: 10,
    },
    infoInnerContainer: {
        gap: 8,
    },
    infoLeftColumn: {
        width: [12, 12, 6, 6, 6],
        printWidth: 7,
    },
    infoLeftColumnContainer: {
        gap: 8,
        css: css`
            ${GridOffset} {
                display: flex;
            }
        `,
    },
    brandItem: {
        width: 12,
    },
    brandStyles: {
        nameText: {
            css: css`
                color: ${getColor("text.main")};
            `,
        },
    },
    productNameItem: {
        width: 12,
        css: css`
            padding: 0 10px 5px 5px !important;
        `,
    },
    numbersItem: {
        width: 12,
        printWidth: 12,
    },
    numbersInnerContainer: {
        gap: 8,
    },
    partItem: {
        width: [12, 6, 4, 4, 4],
        printWidth: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            & > div {
                                display: block;  
                            },
                        `,
                    ],
                    "min",
                )}
        `,
    },
    partStyles: {
        wrapper: {
            css: css`
                display: flex;
                align-items: baseline;
                width: 100%;
                color: ${COLORS.lightBlack} !important;
            `,
        },
        text: {
            size: 14,
            css: css`
                // width: 80%;
            `,
        },
        heading: {
            css: css`
                font-size: 14px !important;
            `,
        },
    },
    myPartItem: {
        width: [12, 6, 4, 4, 4],
        printWidth: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                & > div {
                                    display: block;
                            },
                                `,
                    ],
                    "min",
                )}
        `,
    },
    myPartStyles: {
        wrapper: {
            css: css`
                display: flex;
                align-items: baseline;
                width: 100%;
                color: ${COLORS.lightBlack} !important;
            `,
        },
        text: {
            size: 14,
            css: css`
                // width: 80%;
            `,
        },
        heading: {
            css: css`
                font-size: 14px !important;
            `,
        },
    },
    mgfItem: {
        width: [12, 6, 4, 4, 4],
        printWidth: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                & > div {
                                    display: block;
                            },
                                `,
                    ],
                    "min",
                )}
        `,
    },
    mgfStyles: {
        wrapper: {
            css: css`
                display: flex;
                align-items: baseline;
                width: 100%;
                color: ${COLORS.lightBlack} !important;
            `,
        },
        text: {
            size: 14,
            css: css`
                // width: 80%;
            `,
        },
        heading: {
            css: css`
                font-size: 14px !important;
            `,
        },
    },
    notesItem: {
        width: 12,
    },
    infoRightColumn: {
        width: [12, 12, 6, 6, 6],
        printWidth: 5,
    },
    infoRightColumnContainer: {
        gap: 8,
        css: css`
            ${GridOffset} {
                display: flex;
            }
        `,
    },
    priceItem: {
        width: [6, 6, 12, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    secondaryVatLabelText: {
        size: 12,
    },
    qtyItem: {
        width: [6, 6, 12, 4, 4],
        printWidth: 6,
    },
    totalItem: {
        width: [12, 12, 12, 4, 4],
        printWidth: 6,
        css: css`
            font-weight: 600;
            flex-direction: column;
            @media print {
                padding-right: 0;
            }
        `,
    },
    priceStyles: {
        text: {
            color: "rgb(11, 112, 183)",
            weight: 500,
            size: 14,
        },
        heading: {
            css: css`
                font-size: 14px !important;
                text-transform: uppercase;
            `,
        },
    },
    subtotalWithoutVatHeadingAndText: {
        text: {
            size: 14,
        },
        heading: {
            css: css`
                font-size: 14px !important;
            `,
        },
    },
    qtyStyles: {
        text: {
            size: 14,
        },
        heading: {
            css: css`
                font-size: 14px !important;
                text-transform: uppercase;
            `,
        },
    },
    notesStyles: {
        text: {
            size: 14,
        },
        heading: {
            css: css`
                font-size: 14px !important;
            `,
        },
    },
    totalStyles: {
        text: {
            size: 14,
        },
        heading: {
            css: css`
                font-size: 14px !important;
                text-transform: uppercase;
            `,
        },
    },
    productNameLink: {
        typographyProps: {
            color: `${COLORS.lightBlack} !important`,
            weight: TYPOGRAPHY.weight.bold,
            css: css`
                @media print {
                    font-size: 14px;
                    -webkit-print-color-adjust: exact;
                }
            `,
        },
    },
    container: {
        css: css`
            width: 100%;
        `,
    },
};

const InvoiceDetailsLineCard = ({ invoiceLine, enableVat, vatPriceDisplay, showPricing, extendedStyles }: Props) => {
    const [styles] = React.useState(() => mergeToNew(cardStyles, extendedStyles));

    const isShareEntity = getIsShareEntity();
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.imageItem}>
                <Link href={invoiceLine.productUri} {...styles.imageLink}>
                    <LazyImage src={invoiceLine.mediumImagePath} {...styles.image} isShareEntity={isShareEntity} />
                </Link>
            </GridItem>
            <GridItem {...styles.infoItem}>
                <GridContainer {...styles.infoInnerContainer}>
                    <GridItem {...styles.infoLeftColumn}>
                        <GridContainer {...styles.infoLeftColumnContainer}>
                            {invoiceLine.brand && (
                                <GridItem {...styles.brandItem} data-test-selector="invoiceLine_brand">
                                    <ProductBrand
                                        brand={invoiceLine.brand}
                                        showLogo={false}
                                        extendedStyles={styles.brandStyles}
                                    />
                                </GridItem>
                            )}
                            <GridItem {...styles.productNameItem}>
                                <Link
                                    {...styles.productNameLink}
                                    href={invoiceLine.productUri}
                                    data-test-selector="invoiceLine_product"
                                >
                                    {invoiceLine.shortDescription}
                                </Link>
                            </GridItem>
                            <GridItem {...styles.numbersItem}>
                                <GridContainer {...styles.numbersInnerContainer}>
                                    {invoiceLine.productERPNumber && (
                                        <GridItem {...styles.partItem}>
                                            <SmallHeadingAndText
                                                heading={translate("Part #")}
                                                text={invoiceLine.productERPNumber}
                                                extendedStyles={styles.partStyles}
                                            />
                                        </GridItem>
                                    )}
                                    {invoiceLine.customerProductNumber && (
                                        <GridItem {...styles.myPartItem}>
                                            <SmallHeadingAndText
                                                heading={translate("Cust.Part #")}
                                                text={invoiceLine.customerProductNumber}
                                                extendedStyles={styles.myPartStyles}
                                            />
                                        </GridItem>
                                    )}
                                    {invoiceLine.manufacturerItem && (
                                        <GridItem {...styles.mgfItem}>
                                            <SmallHeadingAndText
                                                heading={translate("Mfg #")}
                                                text={invoiceLine.manufacturerItem}
                                                extendedStyles={styles.mgfStyles}
                                            />
                                        </GridItem>
                                    )}
                                    {invoiceLine?.properties?.kGPSerialNumber && (
                                        <GridItem {...styles.mgfItem}>
                                            <SmallHeadingAndText
                                                heading={translate("Serial #")}
                                                text={invoiceLine.properties.kGPSerialNumber}
                                                extendedStyles={styles.mgfStyles}
                                            />
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </GridItem>
                            {invoiceLine.notes && (
                                <GridItem {...styles.notesItem}>
                                    <SmallHeadingAndText
                                        heading={translate("Line Notes")}
                                        text={invoiceLine.notes}
                                        extendedStyles={styles.notesStyles}
                                    />
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.infoRightColumn}>
                        <GridContainer {...styles.infoRightColumnContainer}>
                            {showPricing && (
                                <GridItem {...styles.priceItem}>
                                    <SmallHeadingAndText
                                        heading={translate("Price")}
                                        text={
                                            (enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                ? invoiceLine.unitPriceWithVatDisplay
                                                : invoiceLine.unitPriceDisplay) +
                                            (invoiceLine.unitOfMeasure ? ` / ${invoiceLine.unitOfMeasure}` : "")
                                        }
                                        extendedStyles={styles.priceStyles}
                                    />
                                    {enableVat && (
                                        <>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {vatPriceDisplay === "DisplayWithVat" ||
                                                vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                    ? `${translate("Inc. VAT")} (${invoiceLine.taxRate}%)`
                                                    : translate("Ex. VAT")}
                                            </Typography>
                                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                <>
                                                    <Typography {...styles.secondaryPriceText} as="p">
                                                        {invoiceLine.unitPriceDisplay +
                                                            (invoiceLine.unitOfMeasure
                                                                ? ` / ${invoiceLine.unitOfMeasure}`
                                                                : "")}
                                                    </Typography>
                                                    <Typography as="p" {...styles.secondaryVatLabelText}>
                                                        {translate("Ex. VAT")}
                                                    </Typography>
                                                </>
                                            )}
                                        </>
                                    )}
                                </GridItem>
                            )}

                            <GridItem {...styles.qtyItem}>
                                <SmallHeadingAndText
                                    heading={translate("QTY invoiced")}
                                    text={invoiceLine.qtyInvoiced}
                                    extendedStyles={styles.qtyStyles}
                                />
                            </GridItem>
                            {showPricing && (
                                <GridItem {...styles.totalItem}>
                                    {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                        <SmallHeadingAndText
                                            heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                            text={invoiceLine.lineTotalDisplay}
                                            extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                                        />
                                    )}
                                    <SmallHeadingAndText
                                        heading={
                                            !enableVat
                                                ? translate("SUBTOTAL")
                                                : vatPriceDisplay !== "DisplayWithoutVat"
                                                  ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                                  : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                        }
                                        text={
                                            enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                ? invoiceLine.netPriceWithVatDisplay
                                                : invoiceLine.lineTotalDisplay
                                        }
                                        extendedStyles={styles.totalStyles}
                                    />
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(InvoiceDetailsLineCard);
